import { useCallback, useEffect, useState } from "react";
import { preloadCompressedVehicleImage } from "@/utils/compressed-vehicle-image";

export function isHeicUrl(url: string): boolean {
  const path = url.split("?")[0].split("#")[0];
  return /\.(heic|heif)$/i.test(path);
}

const HEIC_CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_HEIC_CACHE_ENTRIES = 20;
const heicCache = new Map<string, { promise: Promise<Blob>; expiresAt: number }>();

function pruneHeicCache(now: number) {
  for (const [key, entry] of heicCache) {
    if (entry.expiresAt <= now) heicCache.delete(key);
  }

  while (heicCache.size >= MAX_HEIC_CACHE_ENTRIES) {
    const oldestKey = heicCache.keys().next().value;
    if (!oldestKey) break;
    heicCache.delete(oldestKey);
  }
}

function getConvertedHeicBlob(url: string): Promise<Blob> {
  const now = Date.now();
  const cached = heicCache.get(url);
  if (cached && cached.expiresAt > now) return cached.promise;
  if (cached) heicCache.delete(url);

  const promise = convertHeicToBlob(url);
  heicCache.set(url, { promise, expiresAt: now + HEIC_CACHE_TTL_MS });
  pruneHeicCache(now);

  void promise.catch(() => {
    if (heicCache.get(url)?.promise === promise) heicCache.delete(url);
  });

  return promise;
}

async function convertHeicToBlob(url: string): Promise<Blob> {
  // Use the wasm-bundle variant — it's pre-bundled for browser use and handles
  // all HEIC variants including multi-image sequences (Live Photos, bursts).
  const libheif = (await import("libheif-js/wasm-bundle")).default;

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
  const arrayBuffer = await response.arrayBuffer();

  const decoder = new libheif.HeifDecoder();
  const data = decoder.decode(new Uint8Array(arrayBuffer));

  if (!data || data.length === 0) throw new Error("No images found in HEIC file");

  // Take the first image from the sequence
  const image = data[0];
  const width = image.get_width();
  const height = image.get_height();

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not get canvas 2d context");

  const imageData = context.createImageData(width, height);

  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (displayData: ImageData | null) => {
      if (!displayData) {
        reject(new Error("HEIF processing error"));
        return;
      }
      resolve();
    });
  });

  context.putImageData(imageData, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to convert canvas to blob"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.92
    );
  });
}

export function useHeicImage(imageUrl: string | undefined | null) {
  const [convertedUrl, setConvertedUrl] = useState<string | null>(null);
  // Start loading immediately if we have a url — avoids a paint with no spinner
  const [isLoading, setIsLoading] = useState(!!imageUrl);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setError(null);
    setConvertedUrl(null);

    if (!imageUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    let isCancelled = false;
    let blobUrl: string | null = null;

    const resolve = async () => {
      try {
        const resolvedUrl = await preloadCompressedVehicleImage({ url: imageUrl });

        if (isCancelled) return;

        if (!isHeicUrl(resolvedUrl)) {
          setConvertedUrl(resolvedUrl);
          return;
        }

        const blob = await getConvertedHeicBlob(resolvedUrl);

        if (isCancelled) return;

        blobUrl = URL.createObjectURL(blob);
        setConvertedUrl(blobUrl);
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err : new Error("Failed to convert HEIC image"));
          setConvertedUrl(imageUrl);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void resolve();

    return () => {
      isCancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [imageUrl]);

  const useOriginalUrl = useCallback(() => {
    setConvertedUrl(imageUrl ?? null);
  }, [imageUrl]);

  return { convertedUrl, isLoading, error, useOriginalUrl };
}
